import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  FormControlLabel,
  Checkbox,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { parishService } from '../services';
import { useToast } from '../hook/use-toast';
import { cleanDocument, formatCpfCnpjInput } from '../../../utils/cpfCnpjValidator';
import {
  looksLikePersonName,
  validatePagarmeRecipientForm,
  PAGARME_BANK_HOLDER_NAME_MAX,
  type PagarmeRecipientFormErrors,
} from '../../../utils/pagarmeRecipientFormValidation';

const PIX_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
] as const;

type RegisterType = 'individual' | 'corporation';

const ParishPagarmeReview: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { parishId: routeParishId } = useParams<{ parishId: string }>();
  const [searchParams] = useSearchParams();
  const selectedId = routeParishId || searchParams.get('parishId');

  const reviewQuery = useQuery({
    queryKey: ['parish-pagarme-review', selectedId],
    queryFn: () => parishService.getPagarmeOnboardingReview(selectedId!),
    enabled: !!selectedId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
  });

  const bundle = reviewQuery.data?.data;
  const parishMeta = bundle?.parish as Record<string, unknown> | undefined;

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [cpfCnpjType, setCpfCnpjType] = useState<'cpf' | 'cnpj'>('cpf');

  const [pixKeyType, setPixKeyType] = useState<(typeof PIX_TYPES)[number]['value']>('email');
  const [pixKey, setPixKey] = useState('');

  const [registerType, setRegisterType] = useState<RegisterType>('individual');
  const [birthdate, setBirthdate] = useState('');
  const [sameRepAsHolder, setSameRepAsHolder] = useState(true);

  const [companyName, setCompanyName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [foundingDate, setFoundingDate] = useState('');
  const [repName, setRepName] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repDocument, setRepDocument] = useState('');
  const [repBirthdate, setRepBirthdate] = useState('');
  const [repPhone, setRepPhone] = useState('');

  const [holderName, setHolderName] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [branchCheckDigit, setBranchCheckDigit] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountCheckDigit, setAccountCheckDigit] = useState('');
  const [accountKind, setAccountKind] = useState<'checking' | 'savings'>('checking');

  /** Pagar.me exige holder_document = documento do recebedor (= CPF/CNPJ da paróquia). */
  const parishDocDigits = useMemo(() => cleanDocument(cpfCnpj), [cpfCnpj]);
  const holderDocumentSynced = useMemo(() => {
    const d = parishDocDigits;
    if (d.length === 11 || d.length === 14) return formatCpfCnpjInput(d);
    return '';
  }, [parishDocDigits]);
  const holderDocDigits = parishDocDigits;
  const holderLooksCnpj = holderDocDigits.length === 14;
  const canCopyHolderAsRepresentative = holderDocDigits.length === 11;

  const [streetExtra, setStreetExtra] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhoodExtra, setNeighborhoodExtra] = useState('');
  const [cepExtra, setCepExtra] = useState('');
  const [refPoint, setRefPoint] = useState('');

  const [resetRecipientId, setResetRecipientId] = useState(false);
  const [parishDisplayName, setParishDisplayName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<PagarmeRecipientFormErrors>({});
  const hydrateLockedRef = useRef(false);
  const lastHydrationKeyRef = useRef('');
  const saveSnapshotRef = useRef<Record<string, unknown> | null>(null);

  const effectiveRegisterType: RegisterType =
    cpfCnpjType === 'cnpj' || holderDocDigits.length === 14 ? 'corporation' : registerType;

  const applyParishFormFromServer = (p: Record<string, unknown>) => {
    setEmail(String(p.email ?? ''));
    setParishDisplayName(String(p.name ?? ''));
    setPhone(String(p.phone ?? ''));
    setAddress(String(p.address ?? ''));
    setCity(String(p.city ?? ''));
    setState(String(p.state ?? ''));
    setZipCode(String(p.zipCode ?? ''));
    setCpfCnpj(String(p.cpfCnpj ?? ''));
    const ctHydrate = p.cpfCnpjType === 'cnpj' ? 'cnpj' : 'cpf';
    setCpfCnpjType(ctHydrate);

    const wd = (p.withdrawalData || {}) as Record<string, string>;
    const pxt = wd.pixKeyType as (typeof PIX_TYPES)[number]['value'] | undefined;
    if (pxt && PIX_TYPES.some((t) => t.value === pxt)) setPixKeyType(pxt);
    setPixKey(String(wd.pixKey ?? ''));

    const prof = (p.pagarmeRecipientProfile || {}) as Record<string, unknown>;
    if (prof.registerType === 'individual' || prof.registerType === 'corporation') {
      setRegisterType(ctHydrate === 'cnpj' ? 'corporation' : prof.registerType);
    } else {
      setRegisterType(ctHydrate === 'cnpj' ? 'corporation' : 'individual');
    }
    const ind = (prof.individual || {}) as Record<string, string>;
    setBirthdate(ind.birthdate || '');

    const corp = (prof.corporation || {}) as Record<string, unknown>;
    setAnnualRevenue(String(corp.annualRevenue ?? ''));
    setFoundingDate(String(corp.foundingDate ?? ''));
    const rep = (corp.representative || {}) as Record<string, string>;
    setRepName(rep.name || '');
    setRepEmail(rep.email || String(p.email ?? '').trim());
    setRepDocument(rep.document || '');
    setRepBirthdate(rep.birthdate || '');
    setRepPhone(rep.phone || '');
    const hasRepIdentity = !!(String(rep.name || '').trim() || String(rep.document || '').trim());
    setSameRepAsHolder(!hasRepIdentity);

    const ba = (prof.bankAccount || {}) as Record<string, string>;
    const loadedHolder = (ba.holderName || '').trim();
    const loadedCompany = String(corp.companyName ?? '').trim();
    const loadedTrading = String(corp.tradingName ?? '').trim();
    if (cleanDocument(String(p.cpfCnpj ?? '')).length === 14) {
      const corpName =
        loadedCompany ||
        (loadedHolder && !looksLikePersonName(loadedHolder) ? loadedHolder : '') ||
        loadedTrading;
      setCompanyName(corpName);
      setTradingName(loadedTrading || corpName);
      setHolderName(
        (loadedHolder && loadedHolder.length <= PAGARME_BANK_HOLDER_NAME_MAX
          ? loadedHolder
          : corpName.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)) || loadedHolder.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
      );
    } else {
      setCompanyName(loadedCompany);
      setTradingName(loadedTrading);
      setHolderName(loadedHolder);
    }
    setBankCode(ba.bank || '');
    setBranchNumber(ba.branchNumber || '');
    setBranchCheckDigit(String(ba.branchCheckDigit ?? ''));
    setAccountNumber(ba.accountNumber || '');
    setAccountCheckDigit(String(ba.accountCheckDigit ?? ''));
    if (ba.type === 'savings') setAccountKind('savings');
    else setAccountKind('checking');

    const addr = (prof.addressOverride || {}) as Record<string, string>;
    setStreetExtra(addr.street || '');
    setStreetNumber(addr.streetNumber || '');
    setNeighborhoodExtra(addr.neighborhood || '');
    setCepExtra(addr.zipCode || '');
    setRefPoint(addr.referencePoint || '');

    setResetRecipientId(false);
  };

  const lockFormFromServerHydrate = () => {
    hydrateLockedRef.current = true;
  };

  useEffect(() => {
    if (!selectedId) return;
    const p = bundle?.parish as Record<string, unknown> | undefined;
    if (!p) return;
    if (String(p._id ?? '') !== selectedId) return;
    if (hydrateLockedRef.current) return;

    const hydrationKey = `${selectedId}:${reviewQuery.dataUpdatedAt ?? 0}`;
    if (lastHydrationKeyRef.current === hydrationKey) return;

    lastHydrationKeyRef.current = hydrationKey;
    applyParishFormFromServer(p);
  }, [selectedId, bundle?.parish, reviewQuery.dataUpdatedAt]);

  useEffect(() => {
    if (registerType === 'corporation' && sameRepAsHolder && !canCopyHolderAsRepresentative) {
      setSameRepAsHolder(false);
    }
  }, [registerType, sameRepAsHolder, canCopyHolderAsRepresentative]);

  useEffect(() => {
    if (cpfCnpjType === 'cnpj' || holderDocDigits.length === 14) {
      setRegisterType('corporation');
    }
  }, [cpfCnpjType, holderDocDigits.length]);

  const applyCorporateHolder = (name: string) => {
    lockFormFromServerHydrate();
    setCompanyName(name);
    // Titular bancário: atalho ≤30; usuário pode editar o campo "Titular enviado ao banco".
    setHolderName(name.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
    setFieldErrors((prev) => ({ ...prev, companyName: undefined, holderName: undefined }));
  };

  const effectiveCorporateHolder = useMemo(
    () => holderName.trim() || companyName.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX),
    [companyName, holderName]
  );

  saveSnapshotRef.current = {
    email,
    phone,
    address,
    city,
    state,
    zipCode,
    cpfCnpj,
    cpfCnpjType,
    pixKeyType,
    pixKey,
    registerType: effectiveRegisterType,
    birthdate,
    companyName,
    tradingName,
    annualRevenue,
    foundingDate,
    repName,
    repEmail,
    repDocument,
    repBirthdate,
    repPhone,
    holderName,
    holderDocumentSynced,
    bankCode,
    branchNumber,
    branchCheckDigit,
    accountNumber,
    accountCheckDigit,
    accountKind,
    resetRecipientId,
    streetExtra,
    streetNumber,
    neighborhoodExtra,
    cepExtra,
    refPoint,
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error('Selecione uma paróquia');

      const snap = saveSnapshotRef.current as Record<string, string | boolean> | null;
      if (!snap) throw new Error('Formulário indisponível');

      const snapRegisterType = snap.registerType as RegisterType;
      const snapCompanyName = String(snap.companyName ?? '');
      const snapHolderName = String(snap.holderName ?? '');
      const corpNameTrimmed = snapCompanyName.trim();
      const bankHolderName =
        snapRegisterType === 'corporation'
          ? snapHolderName.trim() || corpNameTrimmed.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
          : snapHolderName.trim();

      if (snapRegisterType === 'corporation') {
        const { errors, blockingMessages } = validatePagarmeRecipientForm({
          registerType: snapRegisterType,
          parishDocDigits: holderDocDigits,
          parishCpfCnpjType: String(snap.cpfCnpjType) as 'cpf' | 'cnpj',
          parishName: parishDisplayName,
          holderName: bankHolderName,
          companyName: corpNameTrimmed,
          tradingName: String(snap.tradingName ?? ''),
          birthdate: String(snap.birthdate ?? ''),
          repBirthdate: String(snap.repBirthdate ?? ''),
          repName: String(snap.repName ?? ''),
          repEmail: String(snap.repEmail ?? ''),
          repDocument: String(snap.repDocument ?? ''),
          sameRepAsHolder: false,
          bankCode: String(snap.bankCode ?? ''),
          branchNumber: String(snap.branchNumber ?? ''),
          accountNumber: String(snap.accountNumber ?? ''),
          accountCheckDigit: String(snap.accountCheckDigit ?? ''),
        });
        setFieldErrors(errors);
        if (blockingMessages.length) throw new Error(blockingMessages[0]);
      }

      setFieldErrors({});
      const addrOverride: Record<string, string> = {};
      const street = String(snap.streetExtra ?? '').trim();
      const streetNum = String(snap.streetNumber ?? '').trim();
      const neighborhood = String(snap.neighborhoodExtra ?? '').trim();
      const cep = String(snap.cepExtra ?? '').trim();
      const ref = String(snap.refPoint ?? '').trim();
      if (street) addrOverride.street = street;
      if (streetNum) addrOverride.streetNumber = streetNum;
      if (neighborhood) addrOverride.neighborhood = neighborhood;
      if (cep) addrOverride.zipCode = cep;
      if (ref) addrOverride.referencePoint = ref;

      const payload: Record<string, unknown> = {
        email: String(snap.email ?? '').trim(),
        phone: String(snap.phone ?? '').trim(),
        address: String(snap.address ?? '').trim(),
        city: String(snap.city ?? '').trim(),
        state: String(snap.state ?? '').trim().toUpperCase().slice(0, 2),
        zipCode: String(snap.zipCode ?? '').trim(),
        cpfCnpj: String(snap.cpfCnpj ?? '').trim(),
        cpfCnpjType: snap.cpfCnpjType,
        withdrawalData: {
          pixKeyType: snap.pixKeyType,
          pixKey: String(snap.pixKey ?? '').trim(),
        },
        pagarmeRecipientProfile: {
          registerType: snapRegisterType,
          individual: {
            birthdate: String(snap.birthdate ?? '').trim(),
          },
          corporation: {
            companyName: corpNameTrimmed || bankHolderName,
            tradingName: String(snap.tradingName ?? '').trim() || corpNameTrimmed || bankHolderName,
            annualRevenue: String(snap.annualRevenue ?? '').trim(),
            foundingDate: String(snap.foundingDate ?? '').trim() || undefined,
            representative: {
              name: String(snap.repName ?? '').trim(),
              email: String(snap.repEmail ?? '').trim(),
              document: String(snap.repDocument ?? '').trim(),
              birthdate: String(snap.repBirthdate ?? '').trim(),
              phone: String(snap.repPhone ?? '').trim() || undefined,
              selfDeclaredRepresentative: true,
            },
          },
          bankAccount: {
            holderName: bankHolderName,
            holderDocument: String(snap.holderDocumentSynced ?? '').trim(),
            bank: String(snap.bankCode ?? '').trim(),
            branchNumber: String(snap.branchNumber ?? '').trim(),
            branchCheckDigit: String(snap.branchCheckDigit ?? ''),
            accountNumber: String(snap.accountNumber ?? '').trim(),
            accountCheckDigit: String(snap.accountCheckDigit ?? ''),
            type: snap.accountKind,
          },
          addressOverride: Object.keys(addrOverride).length ? addrOverride : undefined,
        },
      };
      if (snap.resetRecipientId) payload.resetRecipientId = true;
      return parishService.patchPagarmeOnboardingReview(selectedId, payload);
    },
    onSuccess: (res) => {
      const msg = res.message || 'Salvo';
      if (msg.includes('Nenhuma alteração')) {
        toast({ title: msg, description: 'Nada foi gravado no banco. Verifique o payload ou tente de novo.', variant: 'destructive' });
        return;
      }

      const pendencies = res.data?.pendencies || [];
      const savedParish = res.data?.parish as Record<string, unknown> | undefined;
      if (savedParish) {
        applyParishFormFromServer(savedParish);
        queryClient.setQueryData(['parish-pagarme-review', selectedId], {
          success: true,
          data: res.data,
        });
        lastHydrationKeyRef.current = `${selectedId}:${Date.now()}`;
        hydrateLockedRef.current = true;
      } else {
        queryClient.invalidateQueries({ queryKey: ['parish-pagarme-review', selectedId] });
      }

      if (pendencies.length) {
        toast({
          title: msg,
          description: pendencies.slice(0, 3).join(' · '),
          variant: 'destructive',
        });
      } else {
        toast({ title: msg });
      }
      queryClient.invalidateQueries({ queryKey: ['pagarme-onboarding-pending'] });
      setResetRecipientId(false);
    },
    onError: (e: any) => {
      toast({
        title: e.response?.data?.message || e.message || 'Erro ao salvar',
        variant: 'destructive',
      });
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: () => parishService.sendPagarmeOnboardingEmail(selectedId!),
    onSuccess: () => toast({ title: 'E-mail enviado' }),
    onError: () => toast({ title: 'Falha ao enviar e-mail', variant: 'destructive' }),
  });

  const issuePublicLinkMutation = useMutation({
    mutationFn: () => parishService.issuePagarmePublicOnboardingLink(selectedId!),
    onSuccess: async (res) => {
      const url = res.data?.url;
      const exp = res.data?.expiresAt ? new Date(res.data.expiresAt).toLocaleString('pt-BR') : '';
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast({
            title: res.message || 'Link copiado para a área de transferência',
            description: exp ? `Válido até ${exp}` : undefined,
          });
        } catch {
          toast({
            title: 'Link gerado (copie manualmente)',
            description: url,
          });
        }
      } else {
        toast({ title: res.message || 'Link gerado' });
      }
    },
    onError: (e: any) => {
      toast({
        title: e.response?.data?.message || e.message || 'Erro ao gerar link',
        variant: 'destructive',
      });
    },
  });

  const fieldGrid = useMemo(
    () => ({ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }),
    []
  );

  const displayParishName = String(parishMeta?.name ?? 'Paróquia');
  const onboardingStatusLabel = String(
    (parishMeta?.pagarmeRecipientProfile as Record<string, unknown> | undefined)?.onboardingStatus ?? '—'
  );
  const pagarMeRecipientIdDisplay = String(parishMeta?.pagarMeRecipientId ?? '').trim() || '—';
  const createdAtDisplay = parishMeta?.createdAt
    ? new Date(String(parishMeta.createdAt)).toLocaleString('pt-BR')
    : '—';
  const syncLogs = Array.isArray((parishMeta as any)?.pagarmeRecipientSyncLog)
    ? ((parishMeta as any).pagarmeRecipientSyncLog as Array<Record<string, unknown>>)
    : [];
  /** Alinhado ao backend: só conta como subparóquia se houver referência real ao pai (evita falsos positivos). */
  const parentRef = parishMeta?.parentParishId as unknown;
  const isSubParish =
    parentRef != null &&
    parentRef !== '' &&
    !(typeof parentRef === 'object' && parentRef !== null && Object.keys(parentRef as object).length === 0);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/parishes?tab=recipientApproval')}
        sx={{ mb: 2 }}
      >
        Voltar para Paróquias
      </Button>
      <Typography variant="h4" gutterBottom>
        Revisão Pagarme — {displayParishName !== 'Paróquia' ? displayParishName : 'paróquia'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Com CNPJ, preencha <strong>razão social</strong> (igual ao banco), representante legal e conta bancária antes de
        salvar. O titular enviado ao Pagar.me é derivado da razão social — não use nome de pessoa física. Ao salvar, se o
        recebedor estava <strong>pronto</strong>, volta para rascunho até nova sincronização em <strong>Saque</strong>.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
          {!selectedId ? (
            <Alert severity="warning">
              Paróquia não informada. Volte à{' '}
              <Button component={RouterLink} to="/parishes?tab=recipientApproval" size="small">
                lista de paróquias
              </Button>{' '}
              e abra a revisão a partir da fila ou do status split.
            </Alert>
          ) : reviewQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : reviewQuery.isError ? (
            <Alert severity="error">Não foi possível carregar esta paróquia.</Alert>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                <Typography variant="h6">{displayParishName}</Typography>
                {bundle?.recipientSynced ? (
                  <Chip label="Status: sincronizado" color="success" size="small" />
                ) : (
                  <Chip label={`Status onboarding: ${onboardingStatusLabel}`} color="warning" size="small" variant="outlined" />
                )}
                <Button component={RouterLink} to="/parishes" size="small" variant="text">
                  Lista de paróquias
                </Button>
              </Box>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Resumo (espelha o que você veria no painel Pagarme: identificação do recebedor)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ID interno
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {selectedId}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Nome (cadastro paróquia)
                    </Typography>
                    <Typography variant="body2">{displayParishName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Documento (split)
                    </Typography>
                    <Typography variant="body2">
                      {cpfCnpjType.toUpperCase()} {cpfCnpj || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ID recebedor Pagarme
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {pagarMeRecipientIdDisplay}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Conta bancária (rascunho)
                    </Typography>
                    <Typography variant="body2">
                      {bankCode ? `${bankCode} / ag. ${branchNumber} / conta ${accountNumber}` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Criado em (paróquia)
                    </Typography>
                    <Typography variant="body2">{createdAtDisplay}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Log de sincronização do recebedor (Pagar.me)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Últimas tentativas de validação/sync (sucesso e falha) para auditoria no superadmin.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Quando</TableCell>
                      <TableCell>Modo</TableCell>
                      <TableCell>Ação</TableCell>
                      <TableCell>Resultado</TableCell>
                      <TableCell>Detalhe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            Ainda não há tentativas registradas.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      syncLogs.slice(0, 20).map((log, idx) => {
                        const when = log.attemptedAt ? new Date(String(log.attemptedAt)).toLocaleString('pt-BR') : '—';
                        const mode = String(log.mode || '—');
                        const action = String(log.action || '—');
                        const outcome = String(log.outcome || '—');
                        const detail = String(log.message || log.recipientId || '—');
                        return (
                          <TableRow key={`${String(log.attemptedAt || '')}-${idx}`}>
                            <TableCell>{when}</TableCell>
                            <TableCell>{mode}</TableCell>
                            <TableCell>{action}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={outcome === 'success' ? 'sucesso' : outcome === 'failure' ? 'falha' : outcome}
                                color={outcome === 'success' ? 'success' : outcome === 'failure' ? 'error' : 'default'}
                                variant={outcome === 'success' ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 460, wordBreak: 'break-word' }}>{detail}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {bundle?.pixHint ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Pix cadastrado (mascarado): {bundle.pixHint}
                </Typography>
              ) : null}

              {bundle?.pendencies?.length ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    Pendências (recebedor Pagarme e/ou Pix para saque)
                  </Typography>
                  <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                    {bundle.pendencies.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Validação local ok para envio ao Pagarme. Confira ainda se nome/documento batem com o painel do PSP.
                </Alert>
              )}

              <Box onFocusCapture={lockFormFromServerHydrate}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Documento do recebedor (split)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                CPF da paróquia como PF ou CNPJ como PJ — precisa casar com o tipo em “PF / PJ” abaixo.
              </Typography>
              <Box sx={{ ...fieldGrid, gridTemplateColumns: '140px 1fr' }}>
                <TextField select label="Tipo" value={cpfCnpjType} onChange={(e) => setCpfCnpjType(e.target.value as 'cpf' | 'cnpj')} size="small">
                  <MenuItem value="cpf">CPF</MenuItem>
                  <MenuItem value="cnpj">CNPJ</MenuItem>
                </TextField>
                <TextField label="Número (somente dígitos ou formatado)" value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)} size="small" />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Contato no Pagarme (e-mail e telefone)
              </Typography>
              <Box sx={fieldGrid}>
                <TextField label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} size="small" />
                <TextField label="Telefone (DDD + número)" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Endereço principal (main_address)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Usado no cadastro do recebedor quando não há override parcial abaixo.
              </Typography>
              <Box sx={fieldGrid}>
                <TextField label="Logradouro" value={address} onChange={(e) => setAddress(e.target.value)} size="small" fullWidth sx={{ gridColumn: '1 / -1' }} />
                <TextField label="Cidade" value={city} onChange={(e) => setCity(e.target.value)} size="small" />
                <TextField label="UF" value={state} onChange={(e) => setState(e.target.value)} size="small" inputProps={{ maxLength: 2 }} />
                <TextField label="CEP" value={zipCode} onChange={(e) => setZipCode(e.target.value)} size="small" />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Tipo de recebedor
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                PJ (CNPJ): razão social, representante legal e conta bancária são obrigatórios. PF: informe data de
                nascimento do titular.
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={effectiveRegisterType}
                onChange={(_, v) => {
                  if (!v) return;
                  if (cpfCnpjType === 'cnpj' && v === 'individual') return;
                  lockFormFromServerHydrate();
                  setRegisterType(v);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="corporation">Pessoa jurídica</ToggleButton>
                <ToggleButton value="individual" disabled={cpfCnpjType === 'cnpj'}>
                  Pessoa física
                </ToggleButton>
              </ToggleButtonGroup>

              {effectiveRegisterType === 'corporation' ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Dados da empresa (obrigatório)
                  </Typography>
                  <Box sx={fieldGrid}>
                    <TextField
                      label="Razão social"
                      value={companyName}
                      onChange={(e) => applyCorporateHolder(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ gridColumn: '1 / -1' }}
                      required
                      error={!!fieldErrors.companyName}
                      helperText={
                        fieldErrors.companyName ||
                        'Nome completo da empresa (como no CNPJ). O titular do banco pode ser encurtado abaixo.'
                      }
                    />
                    <TextField
                      label="Nome fantasia"
                      value={tradingName}
                      onChange={(e) => {
                        lockFormFromServerHydrate();
                        setTradingName(e.target.value);
                      }}
                      size="small"
                      helperText="Opcional — se vazio, usamos a razão social."
                    />
                  </Box>
                </>
              ) : null}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Conta bancária (obrigatória no Pagarme)
              </Typography>
              <Box sx={fieldGrid}>
                {effectiveRegisterType === 'corporation' ? (
                  <TextField
                    label="Titular enviado ao banco"
                    value={holderName}
                    onChange={(e) => {
                      lockFormFromServerHydrate();
                      setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
                      setFieldErrors((prev) => ({ ...prev, holderName: undefined }));
                    }}
                    size="small"
                    fullWidth
                    sx={{ gridColumn: '1 / -1' }}
                    required
                    inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
                    error={!!fieldErrors.holderName || effectiveCorporateHolder.length > PAGARME_BANK_HOLDER_NAME_MAX}
                    helperText={
                      fieldErrors.holderName ||
                      (effectiveCorporateHolder.length > PAGARME_BANK_HOLDER_NAME_MAX
                        ? `Máx. ${PAGARME_BANK_HOLDER_NAME_MAX} caracteres (${effectiveCorporateHolder.length}).`
                        : `Nome curto enviado ao Pagar.me/banco (máx. ${PAGARME_BANK_HOLDER_NAME_MAX}). ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`)
                    }
                  />
                ) : (
                  <TextField
                    label="Titular *"
                    value={holderName}
                    onChange={(e) => {
                      lockFormFromServerHydrate();
                      setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
                    }}
                    size="small"
                    required
                    inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
                    helperText={`Máx. ${PAGARME_BANK_HOLDER_NAME_MAX} caracteres. ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`}
                  />
                )}
                <TextField
                  label="Doc. titular"
                  value={holderDocumentSynced}
                  size="small"
                  InputProps={{ readOnly: true }}
                  helperText="Sempre igual ao CPF/CNPJ da paróquia (campo «Número» acima). Exigência Pagar.me — altere só o documento principal da paróquia."
                />
                <TextField label="Código banco" value={bankCode} onChange={(e) => setBankCode(e.target.value)} size="small" />
                <TextField label="Agência" value={branchNumber} onChange={(e) => setBranchNumber(e.target.value)} size="small" />
                <TextField label="Díg. agência" value={branchCheckDigit} onChange={(e) => setBranchCheckDigit(e.target.value)} size="small" />
                <TextField label="Conta" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} size="small" />
                <TextField label="Díg. conta" value={accountCheckDigit} onChange={(e) => setAccountCheckDigit(e.target.value)} size="small" />
                <TextField select label="Tipo conta" value={accountKind} onChange={(e) => setAccountKind(e.target.value as 'checking' | 'savings')} size="small">
                  <MenuItem value="checking">Corrente</MenuItem>
                  <MenuItem value="savings">Poupança</MenuItem>
                </TextField>
              </Box>

              {effectiveRegisterType === 'individual' ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Titular PF (CPF da paróquia)
                  </Typography>
                  <Box sx={fieldGrid}>
                    <TextField
                      label="Data de nascimento (DD/MM/AAAA)"
                      value={birthdate}
                      onChange={(e) => setBirthdate(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ gridColumn: '1 / -1' }}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Representante legal (PJ) *
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Pessoa física que assina pela entidade — diferente da razão social do titular da conta.
                  </Alert>
                  <Box sx={{ ...fieldGrid, mt: 1 }}>
                    <TextField
                      label="Nome *"
                      value={repName}
                      onChange={(e) => {
                        lockFormFromServerHydrate();
                        setRepName(e.target.value);
                      }}
                      size="small"
                      required
                      error={!!fieldErrors.repName}
                      helperText={fieldErrors.repName}
                    />
                    <TextField
                      label="E-mail *"
                      value={repEmail}
                      onChange={(e) => {
                        lockFormFromServerHydrate();
                        setRepEmail(e.target.value);
                      }}
                      size="small"
                      required
                      error={!!fieldErrors.repEmail}
                      helperText={fieldErrors.repEmail}
                    />
                    <TextField
                      label="CPF *"
                      value={repDocument}
                      onChange={(e) => {
                        lockFormFromServerHydrate();
                        setRepDocument(e.target.value);
                      }}
                      size="small"
                      required
                      error={!!fieldErrors.repDocument}
                      helperText={fieldErrors.repDocument}
                    />
                    <TextField label="Telefone" value={repPhone} onChange={(e) => setRepPhone(e.target.value)} size="small" />
                    <TextField
                      label="Nascimento (DD/MM/AAAA) *"
                      value={repBirthdate}
                      onChange={(e) => {
                        lockFormFromServerHydrate();
                        setRepBirthdate(e.target.value);
                      }}
                      size="small"
                      fullWidth
                      sx={{ gridColumn: '1 / -1' }}
                      required
                      error={!!fieldErrors.repBirthdate}
                      helperText={fieldErrors.repBirthdate}
                    />
                  </Box>

                  <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Dados extras da empresa (opcional)</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={fieldGrid}>
                        <TextField label="Faturamento anual (centavos)" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} size="small" />
                        <TextField label="Fundação (DD/MM/AAAA)" value={foundingDate} onChange={(e) => setFoundingDate(e.target.value)} size="small" />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Chave Pix — só para solicitar saque (não entra no POST /recipients)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    O recebedor pode ficar pronto sem Pix. Pix é exigência nossa para saque, não do cadastro de subconta no
                    Pagarme.
                  </Alert>
                  <Box sx={{ ...fieldGrid, gridTemplateColumns: '200px 1fr' }}>
                    <TextField select label="Tipo Pix" value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value as any)} size="small">
                      {PIX_TYPES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField label="Chave Pix" value={pixKey} onChange={(e) => setPixKey(e.target.value)} size="small" />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Override de endereço (opcional)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Sobrescreve partes do endereço enviado ao Pagarme (campos vazios continuam vindo do bloco principal).
                  </Typography>
                  <Box sx={fieldGrid}>
                    <TextField label="Logradouro" value={streetExtra} onChange={(e) => setStreetExtra(e.target.value)} size="small" />
                    <TextField label="Número" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} size="small" />
                    <TextField label="Bairro" value={neighborhoodExtra} onChange={(e) => setNeighborhoodExtra(e.target.value)} size="small" />
                    <TextField label="CEP" value={cepExtra} onChange={(e) => setCepExtra(e.target.value)} size="small" />
                    <TextField label="Ponto de referência" value={refPoint} onChange={(e) => setRefPoint(e.target.value)} size="small" fullWidth sx={{ gridColumn: '1 / -1' }} />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters elevation={0} sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Avançado</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={resetRecipientId}
                        onChange={(e) => setResetRecipientId(e.target.checked)}
                        color="warning"
                      />
                    }
                    label="Limpar ID do recebedor no Pagar.me (força subconta nova na próxima sincronização)"
                  />
                </AccordionDetails>
              </Accordion>

              <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar alterações'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => sendEmailMutation.mutate()}
                  disabled={!selectedId || sendEmailMutation.isPending}
                >
                  Enviar e-mail de regularização
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => issuePublicLinkMutation.mutate()}
                  disabled={!selectedId || issuePublicLinkMutation.isPending}
                  title={
                    isSubParish
                      ? 'Esta paróquia parece ser subgrupo; ao clicar, o servidor só aceita se for paróquia principal.'
                      : 'Gera novo token (invalida o link anterior) e copia a URL'
                  }
                >
                  {issuePublicLinkMutation.isPending ? 'Gerando…' : 'Link do formulário (copiar)'}
                </Button>
              </Box>
              {isSubParish ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Esta conta parece ser <strong>subparóquia</strong> (tem paróquia pai). O link público só pode ser gerado
                  para a <strong>paróquia principal</strong>; se você clicar aqui, a API pode recusar — abra a matriz na
                  lista ou na busca.
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  O link exige login do administrador da paróquia; envie por canal confidencial. Ao gerar de novo, o link
                  anterior deixa de funcionar.
                </Typography>
              )}
              </Box>
            </>
          )}
        </Paper>
    </Box>
  );
};

export default ParishPagarmeReview;
